import { Badge } from '@/components/badge/badge.component';
import { Body3, H3 } from '@/components/typography';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ProjectFarmSummaryType } from '@/modules/Report/Report.schema';

type Props = {
  customerId?: string;
  projectId: string;
  projectFarmSummaries: ProjectFarmSummaryType[];
  enableFarmLinks?: boolean;
};

export const ProjectFarmSummaryTable = ({ customerId, projectId, projectFarmSummaries, enableFarmLinks = true }: Props) => {
  const href = customerId && projectId ? `customers/${customerId}/projects/${projectId}` : '';

  return (
    <div className={cn('flex flex-col my-7 gap-4')}>
      <div className={cn('flex flex-col gap-4')}>
        <H3>Farm Summary</H3>
        <div className={cn('flex flex-col gap-2')}>
          <Body3 className="text-neutral-500">Carbon stock within your project</Body3>
          <div className={cn('flex flex-row gap-5')}>
            <div className={cn('flex items-center gap-2')}>
              <div className={cn('w-4 h-4 rounded-lg bg-green')} />
              <Body3>High</Body3>
            </div>
            <div className={cn('flex items-center gap-2')}>
              <div className={cn('w-4 h-4 rounded-lg bg-yellow')} />
              <Body3>Medium</Body3>
            </div>
            <div className={cn('flex items-center gap-2')}>
              <div className={cn('w-4 h-4 rounded-lg bg-red')} />
              <Body3>Low</Body3>
            </div>
          </div>
        </div>
      </div>
      <div className={cn('flex flex-col')}>
        <div
          className={cn(
            'overflow-y-auto border border-neutral-200 rounded-lg',
            projectFarmSummaries.length < 10 ? 'max-h-[700px]' : 'h-[700px]'
          )}
        >
          <Table className="min-w-full">
            <TableHeader className="sticky-table-header">
              <TableRow>
                <TableHead className={cn('border border-neutral-25 text-left font-semibold w-[12%]')}>Farm ID</TableHead>
                <TableHead className={cn('border border-neutral-25 text-left font-semibold w-[16%]')}>Farm Name</TableHead>
                <TableHead className={cn('border border-neutral-25 text-right font-semibold w-[12%]')}>Total Farm Area (ha)</TableHead>
                <TableHead className={cn('border border-neutral-25 text-right font-semibold w-[12%]')}>Total Cores</TableHead>
                <TableHead className={cn('border border-neutral-25 text-right font-semibold w-[12%]')}>Cores Per Hectare</TableHead>
                <TableHead className={cn('border border-neutral-25 text-right font-semibold w-[12%]')}>SOC%</TableHead>
                <TableHead className={cn('border border-neutral-25 text-right font-semibold w-[12%]')}>Total Carbon Stock (t)</TableHead>
                <TableHead className={cn('border border-neutral-25 text-right font-semibold w-[12%]')}>
                  Average Carbon Stock (t/ha)
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projectFarmSummaries.map((farmSummary, index) => {
                return (
                  <TableRow
                    key={farmSummary.farmId || index}
                    className={cn(
                      enableFarmLinks && 'cursor-pointer transition-all duration-500 hover:bg-neutral-200 hover:shadow-sm hover:font-medium'
                    )}
                    onClick={
                      enableFarmLinks
                        ? () => {
                            if (farmSummary.farmId) {
                              window.location.href = `/${href}/farms/${farmSummary.farmId}`;
                            }
                          }
                        : undefined
                    }
                  >
                    <TableCell className={cn('border border-neutral-25 text-left font-semibold')}>{farmSummary.farmId}</TableCell>
                    <TableCell className={cn('border border-neutral-25 text-left')}>{farmSummary.farmName}</TableCell>
                    <TableCell className={cn('border border-neutral-25 text-right')}>{farmSummary.farmAreaHa}</TableCell>
                    <TableCell className={cn('border border-neutral-25 text-right')}>{farmSummary.coreCount}</TableCell>
                    <TableCell className={cn('border border-neutral-25 text-right')}>{farmSummary.coresPerHectare}</TableCell>
                    <TableCell className={cn('border border-neutral-25 text-right')}>{farmSummary.socPct}</TableCell>
                    <TableCell className={cn('border border-neutral-25 text-right')}>{farmSummary.totalCarbonStockTonnes}</TableCell>
                    <TableCell className={cn('border border-neutral-25 text-right')}>
                      {farmSummary.avgCarbonStock?.value && (
                        <span className="inline-block">
                          <Badge
                            value={farmSummary.avgCarbonStock?.value}
                            color={farmSummary.avgCarbonStock?.color}
                            size="m"
                            textAlign="right"
                          />
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
